import { View, type ViewStyle } from "react-native";
import { Minus, TrendingDown, TrendingUp } from "lucide-react-native";
import { colors } from "../theme/tokens";
import { MoonText } from "./MoonText";
import { MetricLabelText, MetricValueText } from "./MetricText";

/**
 * Number-over-label tile for dashboard / homepage stats. Audit task 2.5.
 *
 * Pattern repeats across PlanHomeScreen / OptimizationCard / CostEstimateCard
 * / MilestoneCelebrationCard — large weighted number + a one-line caption
 * underneath. Hue lives in the number, the caption stays neutral.
 *
 * Second-pass enhancements (sweep/stat-block-second-pass):
 *   - `trend` renders a small arrow next to the number for delta-vs-previous
 *     stat tiles (cycle metric cards). Up = success hue, flat/down = neutral.
 *   - `layout="label-first"` flips to label-over-value order, used by
 *     past-cycle metrics, clinic success tiles, and the homepage cost card
 *     where the caption sits above the headline.
 *   - `unit` renders a small line directly under the value, between value
 *     and caption — covers metric tiles that print "ng/mL" / "mIU/mL".
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / loading — `loading` swaps the number for a `—` placeholder.
 *     A full skeleton lives in MoonSkeleton; consumers wanting a shimmer
 *     while data resolves wrap MoonStatBlock in MoonSkeleton themselves.
 *   - focused / error / disabled — N/A; stat tiles are display-only.
 */
type Props = {
  value: string | number;
  label: string;
  /** Optional small line directly under the value (e.g. "ng/mL"). */
  unit?: string;
  /** Optional caption under the label/unit (e.g. "+ 2 this week"). */
  caption?: string;
  /** Hue applied to the number. Defaults to text (no accent). */
  accentColor?: string;
  /** Show `—` placeholder instead of the value. */
  loading?: boolean;
  align?: "left" | "center";
  /**
   * Render order. Default `value-first` is number-over-label.
   * `label-first` flips to label-over-value (with unit + caption still
   * below the value).
   */
  layout?: "value-first" | "label-first";
  /** Small trend arrow rendered inline next to the number. */
  trend?: "up" | "down" | "flat";
  testID?: string;
  style?: ViewStyle;
};

export function MoonStatBlock({
  value,
  label,
  unit,
  caption,
  accentColor = colors.text,
  loading,
  align = "left",
  layout = "value-first",
  trend,
  testID,
  style,
}: Props) {
  const display = loading ? "—" : String(value);
  const textAlign = align === "center" ? "center" : "left";

  const trendIcon = (() => {
    if (!trend || loading) return null;
    const size = 14;
    const stroke = 2;
    if (trend === "up") {
      return (
        <TrendingUp size={size} color={colors.success} strokeWidth={stroke} />
      );
    }
    if (trend === "down") {
      return (
        <TrendingDown
          size={size}
          color={colors.textLight}
          strokeWidth={stroke}
        />
      );
    }
    return <Minus size={size} color={colors.textLight} strokeWidth={stroke} />;
  })();

  const labelNode = (
    <MetricLabelText style={{ textAlign, textTransform: "none" }}>
      {label}
    </MetricLabelText>
  );

  const valueNode = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "baseline",
        gap: 6,
        justifyContent: align === "center" ? "center" : "flex-start",
      }}
    >
      <MetricValueText
        size="display"
        style={{
          color: loading ? colors.textFaint : accentColor,
        }}
      >
        {display}
      </MetricValueText>
      {trendIcon}
    </View>
  );

  const unitNode = unit ? (
    <MoonText variant="caption" color="textLight" style={{ textAlign }}>
      {unit}
    </MoonText>
  ) : null;

  const captionNode = caption ? (
    <MoonText variant="caption" color="textFaint" style={{ textAlign }}>
      {caption}
    </MoonText>
  ) : null;

  return (
    <View
      testID={testID}
      style={[
        {
          alignItems: align === "center" ? "center" : "flex-start",
          gap: 4,
        },
        style,
      ]}
    >
      {layout === "label-first" ? labelNode : valueNode}
      {layout === "label-first" ? valueNode : labelNode}
      {unitNode}
      {captionNode}
    </View>
  );
}
